import React from 'react';
import EmailReplyComposer from '../../components/EmailReplyComposer';

const API = process.env.REACT_APP_API_URL;

// The email as it was sent (operator order 2026-08-28): envelope, the rendered
// message exactly as the recipient received it, and the .eml to download and
// share. Shared by vendor assessments and internal program audits. Rendered
// in a sandboxed frame — no scripts, no navigation.
//
// Reply all and Follow up (operator order 2026-11-03: one place per audit,
// every email and every reply saved): both open an inline composer on the
// page with the email being answered shown in full beneath, and the platform
// sends it, from the reader's own mailbox when connected, else from the
// compliance mailbox in their name, so it is on the record the moment it
// goes out, or marked failed. The reader may still take the same email to
// their own mail program as an unsent draft; that hand-off is on the record
// too, as "opened in your mail program", and becomes the email as sent when
// the copy reaches the audit address.
//   engagementId  the audit the row belongs to (needed for the reply routes)
//   onSent        called after a successful send (reload the record)
export default function SentEmailView({ c, engagementId = null, onSent = null }) {
  const [open, setOpen] = React.useState(false);
  const [compose, setCompose] = React.useState(null); // null | 'reply' | 'follow_up'
  const canReply = !!(engagementId && c.can_reply && (c.reply_all_to || []).length);
  const draft = (mode) => `${API}/api/fdr/engagements/${engagementId}/correspondence/${c.id}/reply.eml?mode=${mode}`;
  const send = async ({ mode, to, cc, subject, body, files }) => {
    const fd = new FormData();
    fd.append('mode', mode); fd.append('to', to.join(', ')); fd.append('cc', cc.join(', '));
    fd.append('subject', subject); fd.append('body', body);
    for (const f of files || []) fd.append('files', f);
    const r = await fetch(`${API}/api/fdr/engagements/${engagementId}/correspondence/${c.id}/reply`, { method: 'POST', credentials: 'include', body: fd });
    const d = await r.json().catch(() => ({}));
    if (!r.ok || d.success === false) throw new Error(d.message || 'Could not send.');
    if (onSent) onSent();
  };
  const row = (label, value) => value ? (
    <div className="flex gap-2 text-[10.5px]"><span className="w-12 flex-none text-slate-400 uppercase tracking-wide font-semibold">{label}</span><span className="text-slate-600 break-all">{value}</span></div>
  ) : null;
  const when = (v) => (v ? new Date(v).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');
  const status = c.status === 'failed'
    ? { cls: 'bg-rose-50 text-rose-700 ring-rose-200', text: `Not sent${c.send_error ? `: ${c.send_error}` : ''}. Nothing reached the recipients. Send it again or open it in your mail program.` }
    : c.status === 'opened_in_mail_program'
      ? { cls: 'bg-amber-50 text-amber-800 ring-amber-200', text: `Opened in your mail program on ${when(c.created_at)}. The platform has not received your copy on the audit address yet, so it cannot confirm this went out. It becomes the email as sent when the copy arrives.` }
      : c.status === 'sent' && c.via === 'email_from_team'
        ? { cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200', text: 'Sent from your mail program and received on the audit address.' }
        : c.status === 'sent' && c.channel
          ? { cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200', text: c.channel === 'graph_as_user' ? 'Sent from your own mailbox by the platform.' : 'Sent from the compliance mailbox in your name.' }
          : null;
  return (
    <div className="mt-1.5 mb-1 border border-slate-200 rounded-lg overflow-hidden">
      <div className="px-3 py-2 bg-slate-50 space-y-0.5">
        <div className="text-[11px] font-semibold text-slate-700">{c.email_subject || c.subject}</div>
        {status && <div className={`text-[10.5px] font-semibold rounded-md px-2 py-1 ring-1 ${status.cls}`}>{status.text}</div>}
        {row('From', c.from_email)}
        {row('To', c.to_emails)}
        {row('Cc', c.cc_emails)}
        {row('Reply', c.reply_to)}
        <div className="flex items-center gap-3 pt-1 flex-wrap">
          {c.body_html && (
            <button type="button" onClick={() => setOpen(!open)} className="text-[10.5px] font-semibold text-orange-700 hover:text-orange-900">
              {open ? 'Close email' : 'Open email'}
            </button>
          )}
          {c.email_document_id && c.email_available
            ? <a href={`${API}/api/fdr/documents/${c.email_document_id}/download`} className="text-[10.5px] font-semibold text-orange-700 hover:text-orange-900 no-underline">Download email (.eml)</a>
            : c.email_document_id ? <span className="text-[10px] font-semibold text-slate-400">Email file purged</span> : null}
          {canReply && (
            <button type="button" onClick={() => setCompose(compose === 'reply' ? null : 'reply')} className="text-[10.5px] font-semibold text-slate-700 hover:text-slate-900"
              title="Reply to everyone on this email, here on the page. The email goes below your message in full, and the audit's reply address stays on it, so your reply and every answer to it are saved here.">
              Reply all
            </button>
          )}
          {canReply && (
            <button type="button" onClick={() => setCompose(compose === 'follow_up' ? null : 'follow_up')} className="text-[10.5px] font-semibold text-slate-700 hover:text-slate-900"
              title="A new message to the same people, here on the page, with this email below it in full, saved here with every answer to it.">
              Follow up
            </button>
          )}
        </div>
      </div>
      {open && c.body_html && (
        <iframe title="Sent email" sandbox="" srcDoc={c.body_html} className="w-full bg-white" style={{ height: 520, border: 0 }} />
      )}
      {compose && (
        <div className="px-3 pb-3 bg-slate-50">
          <EmailReplyComposer key={compose} mode={compose} to={c.reply_all_to || []}
            subject={compose === 'follow_up' ? c.follow_up_subject : c.reply_subject}
            prior={c} draftHref={draft(compose)} onSend={send} onClose={() => setCompose(null)} />
        </div>
      )}
    </div>
  );
}
